<script>
    import { onMount, onDestroy } from 'svelte';
    import { datepickerRange, getDateRangeFromPreset, resetFlatpickrDate } from '@lib/datepicker/datepickerRange';
    import { formatDateToKorean } from '@lib/util/dateUtils';
    import PopupNotice from '@components/PopupNotice.svelte';
    import { getNotice, modifyNoticeStatus } from '@lib/api/notice';
    import Pagination from '@components/Pagination.svelte';
    import ItemPerPageSelect from '@components/ItemPerPageSelect.svelte';
    import { perPageOptions } from '@lib/models/perPageOptions'
    import { datePresetOptions } from '@lib/models/datePresetOptions'

    let isInitialMount = true;          // 초기 mount 여부 확인
    let isCategoryOpen = false;         // 검색 조건 카테고리 선택 드롭다운 열림 여부
    let isDatePresetOpen = false;       // 날짜 프리셋 선택 드롭다운 열림 여부
    let isItemsPerPageOpen = false;     // 테이블 조회 개수 선택 드롭다운 열림 여부
    let isRegisterPopupOpen = false;    // 공지사항 등록 팝업 열림 여부
    let isModifyPopupOpen = false;      // 공지사항 수정 팝업 열림 여부

    let viewCategory = '';              // 사용자에게 표시할 카테고리명 (ex. 상태, 제목 등)
    let viewDataPreset = '';            // 사용자에게 표시할 날짜 프리셋명 (ex. 오늘, 어제 등)
    let viewItemsPerPage = '';          // 사용자에게 표시할 테이블 조회 개수
    let status = 'all';                 // 초기 선택값

    let categoryDropdownEl = null;      // 카테고리 드롭다운 DOM 요소 참조
    let datePresetDropdownEl = null;    // 날짜 프리셋 드롭다운 DOM 요소 참조
    let viewCountDropdownEl = null;     // 테이블 조회 개수 드롭다운 DOM 요소 참조

    let selectedNotice = null;          // 현재 선택된 공지사항 정보

    let error = '';                     // 에러 메시지 (빈 문자열이면 에러 없음)

    let notices = [];                   // API에서 받아온 공지사항 목록
    let loading = true;                 // 공지사항 데이터 로딩 상태
    let size = 20;                      // 페이지당 공지사항 수 (기본: 20)
    let selectAll = false;              // 전체 선택 상태
    let total = 0;                      // 전체 개수

    let totalPages = 0; 
    let currentPage = 1;

    let startRef = {                    // 시작 날짜 input에 바인딩된 flatpickr 인스턴스 조작용 객체
        onStartChange: (dateStr) => {
            searchParam.startDate = dateStr;
        }
    };

    let endRef = {                      // 종료 날짜 input에 바인딩된 flatpickr 인스턴스 조작용 객체
        onEndChange: (dateStr) => {
            searchParam.endDate = dateStr;
        }
    };

    const filterOptions = [
        { label: '제목', value: 'title' },
        { label: '등록자 이름', value: 'created_name' },
        { label: '수정자 이름', value: 'updated_name' },
    ];

    const statusOptions = [
        { id: 'radio1', label: '전체', value: 'all', checked: true },
        { id: 'radio2', label: '공지중', value: '공지중', checked: false },
        { id: 'radio3', label: '공지종료', value: '공지종료', checked: false }
    ];

    const defaultSearchParam = {
        category: "",
        searchKeyword: "",
        startDate: "",
        endDate: "",
        status: "",
        page: 1,
        size: size
    };

    let searchParam = { ...defaultSearchParam }; // 초기값 세팅

    $: selectAll = notices.length > 0 && notices.every(n => n.checked);
    $: searchParam.status = status;
    
    /**
     * 공지사항 목록을 API로부터 불러와 `notices` 배열에 저장합니다.
     *
     * - `getNotice` API를 호출하여 공지사항 데이터를 조회합니다.
     * - 검색 조건(`searchParam`)을 기반으로 필터링 및 페이징된 데이터를 받아옵니다.
     * - 데이터가 있을 경우 첫 번째 공지사항을 자동으로 선택하여 `selectedNotice`에 저장합니다.
     * - 에러 발생 시 `error` 메시지를 설정하고, 로딩 상태는 항상 정상적으로 해제됩니다.
     *
     * @async
     * @function fetchNotices
     * @param {Object} searchParam - 검색 조건 객체
     * @returns {Promise<void>}
     */
    async function fetchNotices(searchParam) {
        loading = true;
        try {
            const data = await getNotice(searchParam);
            total = data.total;
            notices = data.items.map((/** @type {any} */ item) => ({ ...item, checked: false }));

            totalPages = Math.ceil(total / searchParam.size);
            
            // 첫 번째 플랜 자동 선택
            if (notices.length > 0) {
                selectedNotice = notices[0];
            } else {
                selectedNotice = null;
            }
        } catch (err) {
            console.error('플랜 불러오기 실패:', err);
            error = '플랜 정보를 불러오지 못했습니다.';
        } finally {
            loading = false;
        }
    }

    async function handleNoticeFinish() {
        const selectedItems = notices.filter(n => n.checked);

        if (selectedItems.length === 0) {
            alert('공지사항을 선택해주세요.');
            return;
        }

        try {
            const payload = selectedItems.map(n => ({
                noticeId: n._id
            }));

            await modifyNoticeStatus(payload);

            await fetchNotices(searchParam);

        } catch (e) {
            console.error('공지 종료 실패:', e);
            alert('공지 종료 중 오류가 발생했습니다.');
        }
        }

    // 검색 조건 카테고리 드롭다운 열기/닫기 토글
    function toggleCategoryDropdown() {
        isCategoryOpen = !isCategoryOpen;
    }
    
    // 날짜 프리셋(오늘/이번주 등) 드롭다운 열기/닫기 토글
    function toggleDataPresetDropdown() {
        isDatePresetOpen = !isDatePresetOpen;
    }

    function toggleItemsPerPageDropdown() {
        isItemsPerPageOpen = !isItemsPerPageOpen;
    }

    /**
     * 카테고리 선택 시 호출됨
     * @param {{ label: any; value: any; }} filterOptions
     */
    function selectCategory(filterOptions) {
        viewCategory = filterOptions.label;
        searchParam.category = filterOptions.value;
        isCategoryOpen = false; // 선택 후 드롭다운 닫기
    }

     
    /**
     * 날짜 프리셋(오늘, 어제, 이번 주 등)을 선택했을 때 실행되는 핸들러
     *
     * 1. 프리셋에 맞는 날짜 범위 계산
     * 2. UI 및 내부 상태 업데이트 (viewDataPreset, searchParam 등)
     * 3. flatpickr 인스턴스를 통해 실제 input 요소의 날짜 갱신
     *
     * @param {{ label: string, value: string }} datePresetOptions - 선택된 날짜 프리셋 옵션 객체
     */
    async function selectDatePreset(datePresetOptions) {
        const result = getDateRangeFromPreset(datePresetOptions.value);
       
        if (!result) return;


        viewDataPreset = datePresetOptions.label;
        searchParam.startDate = result.startDate;
        searchParam.endDate = result.endDate;
        isDatePresetOpen = false;


        // Date 객체로 변환
        const start = new Date(result.startDate);
        const end = new Date(result.endDate);

        resetFlatpickrDate(startRef.instance, start);
        resetFlatpickrDate(endRef.instance, end);

    }

    /**
     * 드롭다운 외부 클릭 시 드롭다운을 닫기 위한 이벤트 핸들러
     * @param {{ target: any; }} event 클릭 이벤트 객체
     */
    function handleClickOutside(event) {
        // 통화 드롭다운 외부 클릭 감지
        if (isCategoryOpen && categoryDropdownEl && !categoryDropdownEl.contains(event.target)) {
            isCategoryOpen = false;
        }

        if (isDatePresetOpen && datePresetDropdownEl && !datePresetDropdownEl.contains(event.target)) {
            isDatePresetOpen = false;
        }
    }

    function searchBoxReset(){
        // searchParam 초기화
        searchParam = { ...defaultSearchParam };

        // UI 관련 리셋 로직 실행
        selectCategory(filterOptions[0]);
        selectDatePreset(datePresetOptions[0]);

        status = 'all';

    }

    function search(){
        fetchNotices(searchParam);
    }

    /**
     * 공지사항 등록 팝업을 엽니다.
     * 사용자 UI 상에서 "공지사항 등록" 버튼 클릭 시 호출됩니다.
     */
    function openRegisterPopup() {
        isRegisterPopupOpen = true;
    }

    /**
     * 공지사항 등록 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeRegisterPopup() {
        isRegisterPopupOpen = false;
    }

    /**
     * 공지사항 수정 팝업을 엽니다.
     * 예: "수정" 버튼 클릭 시 해당 공지사항 데이터를 불러온 후 호출됩니다.
     */
    function openModifyPopup() {
        isModifyPopupOpen = true;
    }

    /**
     * 공지사항 수정 팝업을 닫습니다.
     * 팝업 내 '닫기' 버튼 또는 외부 클릭 등으로 트리거됩니다.
     */
    function closeModifyPopup() {
        isModifyPopupOpen = false;
    }  

    /**
     * 공지사항 등록 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleSave() {
        fetchNotices(searchParam);
        isRegisterPopupOpen = false;
    }

    /**
     * 공지사항 수정 팝업에서 저장 후 닫기 동작을 처리합니다.
     */
    function handleModify() {
        fetchNotices(searchParam);
        isModifyPopupOpen = false;
    }

    /**
     * 테이블에서 선택된 공지사항을 상태에 반영합니다.
     * @param {object} notice - 선택한 공지사항 객체
     */
    function selectNotice(notice) {
        selectedNotice = notice;

        openModifyPopup();
    }

    /**
     * @param {object} viewCountObject
     */
    function selectItemsPerPage(viewCountObject) {
        viewItemsPerPage = viewCountObject.label;
        size = viewCountObject.value;
        searchParam.size = viewCountObject.value;
        searchParam.page = 1;
        isItemsPerPageOpen = false;

        // 초기 mount일 때는 호출하지 않음
        if (!isInitialMount) {
            fetchNotices(searchParam);
        }

    }
    // 전체 선택 toggle
    function toggleSelectAll() {
        notices = notices.map(n => ({ ...n, checked: selectAll }));
    }

    /**
     * @param {number} page
     */
    function handlePageChange(page) {
        currentPage = page;
        searchParam.page = page;
        fetchNotices(searchParam);
    }

    function handleSelect(option) {
        selectItemsPerPage(option);
    }

    onMount(async () => {
        document.addEventListener('click', handleClickOutside);
        selectCategory(filterOptions[0]);
        selectDatePreset(datePresetOptions[0]);
        selectItemsPerPage(perPageOptions[0]);
        fetchNotices(searchParam);

        isInitialMount = false; // 초기호출 수행 시 false로 반환
    });

    onDestroy(() => {
        document.removeEventListener('click', handleClickOutside);
    });
</script>

<div class="notice-wrap d-flex flex-column h-100">
    <p class="font-20 font-weight-700">공지사항</p>
    <section class="box-gray d-flex align-center mt-5">
        <article class="form-block">
            <form action="" class="d-flex align-center flex-wrap gap-3">
                <fieldset class="width-160">
                    <legend>사용자 관리 input 셀렉트</legend>
                    <div class="custom-select w-100" class:active={isCategoryOpen} bind:this={categoryDropdownEl}>
                        <button type="button" class="custom-select__selected" on:click={toggleCategoryDropdown}>
                            <span>{viewCategory}</span>
                            <i class="icon icon-dropdown"></i>
                        </button>
                        <ul class="custom-select__options">
                            {#each filterOptions as option}
                            <li class="custom-select__option">
                                <button
                                    type="button"
                                    class="custom-select__button"
                                    on:click={() => selectCategory(option)}
                                    >
                                    {option.label}
                                </button>
                            </li>
                            {/each}
                        </ul>
                    </div>
                </fieldset>
                <fieldset class="width-200">
                    <legend>사용자 관리 input 텍스트</legend>
                    <label for="input-field-notice">
                        <input 
                            type="text" 
                            name="" 
                            id="input-field-notice" 
                            class="input-field" 
                            title="notice input 텍스트" 
                            placeholder="" bind:value={searchParam.searchKeyword}
                            on:keydown={(e) => {
                                if (e.key === 'Enter') {
                                e.preventDefault();
                                search();
                                }
                            }}
                            >
                    </label>
                </fieldset>

            </form>
        </article>
        <article class="form-block ml-12">
            <p class="form-block__title">기간 검색</p>
            <form action="" class="d-flex align-center ml-5 gap-3">
                <fieldset>
                    <legend>input style default input 달력</legend>
                    <div class="d-flex align-items-center flex-row js-datepicker-range" style="height: 40px;">
                    <div class="ui-form-block h-100">
                        <input
                        type="text"
                        class="input-field h-100 js-datepicker-range__min"
                        title="시작 날짜"
                        use:datepickerRange={{ type: 'start', ref: startRef }}
                        />
                    </div>
                    <span class="el-hyphen d-flex align-center mx-3">~</span>
                    <div class="ui-form-block h-100">
                        <input
                        type="text"
                        class="input-field h-100 js-datepicker-range__max"
                        title="종료 날짜"
                        use:datepickerRange={{ type: 'end', ref: endRef }}
                        />
                    </div>
                    </div>

                </fieldset>
                <fieldset class="width-80">
                    <legend>요일 input 셀렉트</legend>
                    <div class="custom-select w-100" class:active={isDatePresetOpen} bind:this={datePresetDropdownEl}>
                         <button type="button" class="custom-select__selected" on:click={toggleDataPresetDropdown}>
                            <span>{viewDataPreset}</span>
                            <i class="icon icon-dropdown"></i>
                        </button>
                        <ul class="custom-select__options">
                            {#each datePresetOptions as option}
                            <li class="custom-select__option">
                                <button
                                    type="button"
                                    class="custom-select__button"
                                    on:click={() => selectDatePreset(option)}
                                    >
                                    {option.label}
                                </button>
                            </li>
                            {/each}
                        </ul>
                    </div>
                </fieldset>
            </form>
        </article>
        <article class="form-block ml-12">
            <p class="form-block__title">상태</p>
            <form action="" class="d-flex align-center flex-wrap gap-3 ml-5">
                {#each statusOptions as option, index}
                <fieldset class={index > 0 ? 'ml-5' : ''}>
                    <legend>1:1문의 상태 input 라디오</legend>
                    <div class="custom-radio inline-text">
                    <input
                        type="radio"
                        name="radio-inquiry"
                        id={"input-field-adminRadio" + (index + 1)}
                        bind:group={status}
                        value={option.value}
                        checked={option.checked}
                        title="default input 라디오"
                    />
                    <label for={"input-field-adminRadio" + (index + 1)}>
                        {option.label}
                    </label>
                    </div>
                </fieldset>
                {/each}
            </form>
        </article>
        <article class="form-block ml-auto">
            <button type="button" class="btn btn-reset" aria-label="reset" on:click={searchBoxReset}>
                <i class="icon icon-reset"></i>
            </button>
            <button type="button" class="btn bg-darkgray height-40 ml-1" on:click={search}>조회</button>
        </article>
    </section>
    <div class="d-flex align-center mt-5">
        <p class="table-count">
            총 <span>{total}</span> 건
        </p>
        <button type="button" class="btn border-blue ml-auto height-40" on:click={handleNoticeFinish}>공지 종료</button>
        <button type="button" class="btn bg-blue ml-1 height-40" data-target="popup-notice-apply" on:click={openRegisterPopup}>공지 등록</button>
        <form action="" class="d-flex align-center flex-wrap gap-3 ml-1">
            <fieldset class="width-120">
                <legend>사용자 관리 테이블 개수 input 셀렉트</legend>
                <div class="custom-select w-100" class:active={isItemsPerPageOpen} bind:this={viewCountDropdownEl}>
                    <button type="button" class="custom-select__selected" on:click={toggleItemsPerPageDropdown}>
                        <span>{viewItemsPerPage}</span>
                        <i class="icon icon-dropdown"></i>
                    </button>
                    <ItemPerPageSelect options={perPageOptions} onSelect={handleSelect} />
                </div>
            </fieldset>
        </form>
    </div>
    <div class="d-flex flex-column h-100 mt-2 bg-white box-border">
        <div class="table-wrap">
            <table class="table-basic table-basic-notice">
                <caption></caption>
                <colgroup>
                    <col style="width:5%">
                    <col style="width:5%">
                    <col style="width:10%">
                    <col style="width:auto">
                    <col style="width:10%">
                    <col style="width:15%">
                    <col style="width:10%">
                    <col style="width:15%">
                </colgroup>
                <thead>
                <tr>
                    <th scope="col">
                        <div class="custom-checkBox">
                            <form action="">
                                <fieldset>
                                    <legend></legend>
                                    <input 
                                        type="checkbox" 
                                        name="" 
                                        id="input-exam-checkboxAll" 
                                        title="공지사항 입력 전체 checkbox" 
                                        class="input-checkBoxAll"
                                        bind:checked={selectAll}
                                        on:change={toggleSelectAll}
                                    >
                                    <label for="input-exam-checkboxAll">
                                        <span>공지사항 입력 전체 checkbox</span>
                                    </label>
                                </fieldset>
                            </form>
                        </div>
                    </th>
                    <th scope="col">번호</th>
                    <th scope="col">상태</th>
                    <th scope="col">제목</th>
                    <th scope="col">등록자 이름</th>
                    <th scope="col">등록일시</th>
                    <th scope="col">수정자 이름</th>
                    <th scope="col">수정일시</th>
                </tr>
                </thead>
                <tbody>
                    {#if loading}
                    <tr>
                    <td colspan="6" class="text-center">로딩 중...</td>
                    </tr>
                    {:else if error}
                        <tr>
                        <td colspan="6" class="text-center text-red">{error}</td>
                        </tr>
                    {:else if notices.length === 0}
                        <tr>
                        <td colspan="6" class="text-center">등록된 플랜이 없습니다.</td>
                        </tr>
                    {:else}
                        {#each notices as notice, index}
                        <tr >
                        <td>
                            <div class="custom-checkBox">
                            <fieldset>
                                <legend></legend>
                                <input
                                type="checkbox"
                                class="input-checkBox"
                                bind:checked={notice.checked}
                                id={"input-notice-checkbox-" + notice._id}
                                title={"공지사항 체크박스 " + (index + 1)}
                                />
                                <label for={"input-notice-checkbox-" + notice._id}>
                                <span>공지사항 체크박스 {index + 1}</span>
                                </label>
                            </fieldset>
                            </div>
                        </td>

                        <td>{index + 1}</td>

                        <td>
                            <button
                                type="button"
                                class={"btn " + (notice.status === '공지중' ? 'btn-processing' : 'btn-cancel')}
                            >
                            {notice.status}
                            </button>
                        </td>

                        <td class="text-left">
                            <button data-target="popup-notice-edit" on:click={() => selectNotice(notice)}>
                                {notice.title}
                            </button>
                        </td>

                        <td>{notice.created_name}</td>
                        <td>{formatDateToKorean(notice.created_at)}</td>
                        <td>{notice.updated_name}</td>
                        <td>{formatDateToKorean(notice.updated_at)}</td>
                        </tr>
                        {/each}
                    {/if}
                </tbody>
            </table>
        </div>
    </div>
    <Pagination {currentPage} {totalPages}  pageChange={handlePageChange}></Pagination>
</div>

{#if isRegisterPopupOpen}
  <PopupNotice mode="create" onClose={closeRegisterPopup} onSave={handleSave} />
{/if}

{#if isModifyPopupOpen}
  <PopupNotice mode="modify" initialData={selectedNotice} onClose={closeModifyPopup} onSave={handleModify} />
{/if}